/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react'
import { Paper, CircularProgress, Box, Typography, List, ListItem } from '@mui/material'
import { toDomain } from '~/mappers/surveillance-reporting-mapper'
import { useHookstate } from '@hookstate/core'
import { ISurveillanceReportingReponse } from '../../interfaces/ISurveillanceReporting'
import useDispatchState from '~/features/Dispatch/stores/DispatchState'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useLang } from '~/hooks/useLang'
import { listSurveillanceReportingByDispatchId } from '../../services'
import ReportMenuList from './components/ReportMenuList'
import { ReportComment } from './components/ReportComment'

interface IVideoMonitoringCards {
  readonly onlyCurrentOccurrence?: boolean
  readonly height?: number
}

export default function VideoMonitoringCards({
  onlyCurrentOccurrence = false,
  height,
}: IVideoMonitoringCards) {
  const { translate } = useLang()
  const { dispatch } = useDispatchState()
  const { id: dispatchId } = dispatch()
  
  // Estado local usando hookstate para os reports
  const reportsState = useHookstate<readonly ISurveillanceReportingReponse[]>([])
  const [loading, setLoading] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const disabledAssociate = useHookstate<number | null>(null)
  
  const open = Boolean(anchorEl)
  const idMenu = open ? 'menu-report' : ''

  const loadReports = async (reset = false) => {
    if (isFetching) return
    setIsFetching(true)
    setLoading(true)
    
    const currentReports = reportsState.get()
    const pageQuery = reset ? 0 : Math.floor(currentReports.length / 5)

    try {
      const response = await listSurveillanceReportingByDispatchId({
        dispatchId: dispatchId.get(),
        page: pageQuery,
      })
      
      
      if (response?.data) {
        const toDomainsReport = response.data.map((report: unknown) =>
          toDomain(report as ISurveillanceReportingReponse)
        )
        
        if (reset) {
          reportsState.set(toDomainsReport)
        } else {
          reportsState.set([...currentReports, ...toDomainsReport])
        }
        
        setRowCount(response.rowsCount || response.data.length)
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
    } finally {
      setLoading(false)
      setIsFetching(false)
    }
  }

  // Carregar dados quando o componente montar ou o dispatchId mudar
  useEffect(() => {
    if (dispatchId.get()) {
      loadReports(true) // Reset = true para carregar dados frescos
    }
  }, [dispatchId.get()])

  const reports = reportsState.get()

  return (
    loading ? (
      <Box
        sx={{
          width: '100%',
          height: height || 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    ) : (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: !reports.length ? 'center' : 'flex-start',
          borderRadius: 1,
        }}
      >
        {reports.length === 0 && !loading ? (
          <Box
            sx={{
              width: '100%',
              height: height || 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography>
              {translate('There are no reports')}
            </Typography>
          </Box>
        ) : (
          <List dense={true} sx={{ width: '100%' }}>
            <InfiniteScroll
              dataLength={reports.length}
              next={() => loadReports(false)}
              hasMore={rowCount > 0 && reports.length < rowCount}
              height={height || 400}
              style={{ paddingBottom: '20px' }}
              loader={
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              {reports.map((report, index) => (
                <ListItem sx={{ padding: '5px 8px' }} key={report.id}>
                  <ReportComment
                    report={reportsState[index]}
                    showCheckbox={false}
                    handleClickMenu={(event) => {
                      setAnchorEl(event?.currentTarget)
                      disabledAssociate.set(1)
                    }}
                  />
                </ListItem>
              ))}
            </InfiniteScroll>
          </List>
        )}
        <ReportMenuList
          handleEditReport={() => {}}
          handleAssociateReport={() => {}}
          idMenu={idMenu}
          handleClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          disabledAssociate={disabledAssociate.get()}
        />
      </Paper>
    )
  )
}
